import { inject } from "@angular/core";
import { signalStore, withState, withMethods, patchState } from "@ngrx/signals";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { APP_CONFIG } from "@application/config";
import { DxOdataStoreService } from "@shared/components/dx-data-grid/data-access";
import { DataSource } from "devextreme/common/data";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import { SettingsHelpdeskService } from "../infra/helpdesk.service";
import { IQuickAnswer } from "../entities/helpdesk.interface";
import { tapResponse } from "@ngrx/operators";
import { HttpErrorResponse } from "@angular/common/http";

export interface ISettingsHepdesk {
    helpdeskId: string;
    showQuickAnswer: boolean;
    quickAnswer: IQuickAnswer;
}


export const HelpdeskInitialState: ISettingsHepdesk = {
    helpdeskId: null,
    showQuickAnswer: false,
    quickAnswer: null
};

export const SettingsHelpdeskStore = signalStore(
    { providedIn: 'root' },
    withState(HelpdeskInitialState),
    withMethods((
        store,
        helpdeskService = inject(SettingsHelpdeskService),
        appConfig = inject(APP_CONFIG), dxOdataStoreService = inject(DxOdataStoreService),
        errorStore = inject(ErrorStore)) => {

        return {
            toggleQuickAnswerModal: (showQuickAnswer = !store.showQuickAnswer()) => {
                patchState(store, { showQuickAnswer });
            },

            getQuickAnswersDataSource: (): DataSource => {
                return dxOdataStoreService.fromOdata({
                    url: appConfig.apiUrl.odata, metadata: 'RespostasRapidas', key: 'RespostaRapidaId', params: {
                        $select: 'Atalho,Descricao,RespostaRapidaId'
                    }
                });
            },

            getQuickAnswer: rxMethod<string>(pipe(
            tap(() => patchState(store, setPropLoading('getQuickAnswer'))),
            switchMap((payload) => helpdeskService.getQuickAnswer(payload).pipe(
                tapResponse({
                next: (quickAnswer) => {
                    patchState(store, { quickAnswer, ...setPropLoaded('getQuickAnswer') })
                },
                error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('getQuickAnswer', error));
                }
                })
            )))),

            createQuickAnswer: rxMethod<IQuickAnswer>(pipe(
                tap(() => patchState(store, setPropLoading('createQuickAnswer'))),
                switchMap((payload) => helpdeskService.createQuickAnswer(payload).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, { ...setPropLoaded('createQuickAnswer') })
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, setPropError('createQuickAnswer', error));
                        }
                    })
                ))
            )),

            updateQuickAnswer: rxMethod<IQuickAnswer>(pipe(
                tap(() => patchState(store, setPropLoading('updateQuickAnswer'))),
                switchMap((payload) => helpdeskService.updateQuickAnswer(payload).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, { ...setPropLoaded('updateQuickAnswer') })
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, setPropError('updateQuickAnswer', error));
                        }
                    })
                ))
            )),

            deleteQuickAnswer: rxMethod<string>(pipe(
                tap(() => patchState(store, setPropLoading('deleteQuickAnswer'))),
                switchMap((payload) => helpdeskService.deleteQuickAnswer(payload).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, { ...setPropLoaded('deleteQuickAnswer') })
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, setPropError('deleteQuickAnswer', error));
                        }
                    })
                ))
            )),

            setPropsInit(props: string[]): void {
                patchState(store, { ...setPropInit(...props) });
            },
        }
    }),
    withReqState('getQuickAnswer', 'createQuickAnswer', 'updateQuickAnswer', 'deleteQuickAnswer'),
);
